import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, debounceTime, distinctUntilChanged, map, of, switchMap } from 'rxjs';

export interface AresCompanyResult {
  ico: string;
  obchodniJmeno: string;
  sidlo: AresAddress;
  pravniForma: string;
  stavSubjektu: string;
}

export interface AresAddress {
  kodStatu?: string;
  nazevStatu?: string;
  kodKraje?: number;
  nazevKraje?: string;
  kodOkresu?: number;
  nazevOkresu?: string;
  kodObce?: number;
  nazevObce?: string;
  kodCastiObce?: number;
  nazevCastiObce?: string;
  kodUlice?: number;
  nazevUlice?: string;
  cisloDomovni?: number;
  cisloOrientacni?: number;
  psc?: string;
  textovaAdresa?: string;
}

interface AresResponse {
  pocetCelkem: number;
  ekonomickeSubjekty: AresEkonomickySubjekt[];
}

interface AresSearchRequest {
  start: number;
  pocet: number;
  razeni: string[];
  ico?: string[];
  obchodniJmeno?: string;
}

interface AresEkonomickySubjekt {
  ico: string;
  icoId?: string;
  obchodniJmeno: string;
  pravniForma?: string;
  financniUrad?: string;
  datumVzniku?: string;
  datumZaniku?: string;
  datumAktualizace?: string;
  dic?: string;
  sidlo?: AresAddress;
  adresaDorucovaciSubjekt?: unknown;
  czNace?: string[];
  dalsiUdaje?: unknown[];
  seznamRegistraci?: {
    stavZdrojeVr?: string;
    stavZdrojeRes?: string;
    stavZdrojeRzp?: string;
  };
  primarniZdroj?: string;
}

const ICO_REGEX = /[0-9]{8}/;

@Injectable({
  providedIn: 'root',
})
export class AresService {
  private http = inject(HttpClient);
  private readonly ARES_BASE_URL =
    'https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/vyhledat';

  /**
   * Vyhledá ekonomické subjekty podle IČO nebo názvu
   * Používá oficiální ARES API
   *
   * Příklad funkčního requestu:
   * POST https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/vyhledat
   * {
   *   "start": 0,
   *   "pocet": 10,
   *   "razeni": ["ico"],
   *   "obchodniJmeno": "praceprofirmy"
   * }
   */
  searchCompanies(query: string): Observable<AresCompanyResult[]> {
    // Určí typ vyhledávání podle obsahu query
    const trimmedQuery = query.trim();

    // Základní struktura requestu podle funkčního příkladu
    const requestBody: AresSearchRequest = {
      start: 0,
      pocet: 10,
      razeni: ['ico'],
    };

    if (this.isValidIco(trimmedQuery)) {
      requestBody.ico = [trimmedQuery];
    } else {
      requestBody.obchodniJmeno = trimmedQuery;
    }

    console.log('ARES request:', requestBody);

    return this.http.post<AresResponse>(this.ARES_BASE_URL, requestBody).pipe(
      map(response => {
        console.log('ARES response:', response);

        if (!response?.ekonomickeSubjekty || !Array.isArray(response.ekonomickeSubjekty)) {
          console.log('Žádné ekonomické subjekty v odpovědi');
          return [];
        }

        return response.ekonomickeSubjekty
          .filter(subjekt => {
            // Kontrola aktivního stavu - může být v seznamRegistraci
            const isActive =
              subjekt.seznamRegistraci?.stavZdrojeVr === 'AKTIVNI' ||
              subjekt.seznamRegistraci?.stavZdrojeRes === 'AKTIVNI' ||
              subjekt.seznamRegistraci?.stavZdrojeRzp === 'AKTIVNI' ||
              !subjekt.datumZaniku; // Pokud nemá datum zániku, považujeme za aktivní
            return isActive;
          })
          .map(subjekt => ({
            ico: subjekt.ico,
            obchodniJmeno: subjekt.obchodniJmeno,
            sidlo: subjekt.sidlo || {
              nazevObce: '',
              textovaAdresa: 'Adresa není k dispozici',
            },
            pravniForma: subjekt.pravniForma || '',
            stavSubjektu: 'AKTIVNI',
          }));
      })
    );
  }

  /**
   * Získá detail ekonomického subjektu podle IČO
   */
  getCompanyDetail(ico: string): Observable<AresCompanyResult | null> {
    if (!this.isValidIco(ico)) {
      return of(null);
    }

    const requestBody: AresSearchRequest = {
      start: 0,
      pocet: 1,
      razeni: ['ico'],
      ico: [ico],
    };

    return this.http.post<AresResponse>(this.ARES_BASE_URL, requestBody).pipe(
      map(response => {
        if (!response?.ekonomickeSubjekty || response.ekonomickeSubjekty.length === 0) {
          return null;
        }

        const subjekt = response.ekonomickeSubjekty[0];
        return {
          ico: subjekt.ico,
          obchodniJmeno: subjekt.obchodniJmeno,
          sidlo: subjekt.sidlo || {
            nazevObce: '',
            textovaAdresa: 'Adresa není k dispozici',
          },
          pravniForma: subjekt.pravniForma || '',
          stavSubjektu: 'AKTIVNI',
        };
      })
    );
  }

  /**
   * Formátuje adresu z ARES dat do čitelného formátu
   */
  formatAddress(sidlo: AresAddress): {
    street: string;
    houseNumber: string;
    city: string;
    postalCode: string;
    fullAddress: string;
  } {
    const street = sidlo?.nazevUlice || '';
    const houseNumber = this.formatHouseNumber(sidlo?.cisloDomovni, sidlo?.cisloOrientacni);
    const city = sidlo?.nazevObce || '';
    const postalCode = sidlo?.psc || '';

    // Sestaví celou adresu
    let fullAddress = '';
    if (street && houseNumber) {
      fullAddress = `${street} ${houseNumber}`;
    } else if (houseNumber && city) {
      fullAddress = `${city} ${houseNumber}`;
    } else if (city) {
      fullAddress = city;
    }

    if (city && street && houseNumber) {
      fullAddress = `${street} ${houseNumber}, ${city}`;
    } else if (street && city) {
      fullAddress = `${street}, ${city}`;
    }

    if (postalCode && fullAddress) {
      fullAddress += ` ${postalCode}`;
    }

    return {
      street,
      houseNumber,
      city,
      postalCode,
      fullAddress: fullAddress.trim() || sidlo?.textovaAdresa || 'Adresa není k dispozici',
    };
  }

  /**
   * Formátuje číslo popisné a orientační do standardního formátu
   */
  private formatHouseNumber(cisloDomovni?: number, cisloOrientacni?: number): string {
    if (cisloDomovni && cisloOrientacni) {
      return `${cisloDomovni}/${cisloOrientacni}`;
    }
    if (cisloDomovni) {
      return cisloDomovni.toString();
    }
    if (cisloOrientacni) {
      return cisloOrientacni.toString();
    }
    return '';
  }

  /**
   * Vyhledávání s debouncing pro autocomplete
   */
  getSearchSuggestions(searchTerm$: Observable<string>): Observable<AresCompanyResult[]> {
    return searchTerm$.pipe(
      debounceTime(300), // Čeká 300ms po ukončení psaní
      distinctUntilChanged(), // Pouze pokud se hodnota změnila
      switchMap(term => this.searchCompanies(term))
    );
  }

  /**
   * Kontroluje, zda je řetězec validní IČO
   */
  isValidIco(ico: string): boolean {
    return ICO_REGEX.test(ico);
  }
}
